// Libraries
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import moment, { Moment } from 'moment';

// MUI
import {
    Tooltip, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import { ResourceTextApplication, paramRepository } from '../../core/resources';
import { IFormValidator } from '../../core/FormValidator';
import contextUtils from '../../core/utils.context';
import auth from '../../core/authorization';

// Common
import {
    ADialog,
    ADialogActions,
    ADialogButtonProp,
    ADialogButtonsProp,
    ADialogContent,
    ADialogTitle,
} from '../../common/dialog';
import { ADropdown, AEmailAddressInput, AInputContainer, AInputItem, ARadioButtonGroupInputValue, ARadioButtons, ATextInput, ATextarea } from '../../common/inputs';
import { useAppSelector } from '../../store/hooks';

// Interfaces
import { IFarmDetails } from '../../interfaces/IBusinessEntities';
import { AliasTypeEnum, ProducerReturnOptionResourceText } from '../../interfaces/enums';
import { IAnimalSummary } from '../../interfaces/IAnimal';
import { IProducerReturn } from '../../interfaces/IProducerReturn';
import { GUIDType } from '../../interfaces/types';

// Api
import producerReturnApi from '../../api/producerReturnApi';
import printingApi from '../../api/printingApi';

// Features

export interface IProducerReturnDialogProps {
    data: IProducerReturn | null;
    farm: IFarmDetails;
    announcementId: GUIDType;
    selectedAnimalCollectDate: Moment;
    selectedAnimalEuIdentifier: string|null;
    onClose: (cancelled?: boolean) => any;
    validator: IFormValidator;
    isNew: boolean;
    readonly?: boolean;
}

type ProducerReturnQuestion = {
    type: number;
    optionParameterName: 'MeatCutStyle' | 'ReturnChoice';
    defaultValue: number;
};

enum MeatCutStyleEnum {
    PremiumCuts = 0, 
    GroundMeat = 1   
};

enum ReturnChoiceEnum {
    Take = 0,
    DontTake = 1
};

export default function ProducerReturnDialog(props: IProducerReturnDialogProps) {
    const { data, farm, announcementId, selectedAnimalEuIdentifier, onClose, validator, isNew, readonly } = props;

    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaProducerReturn', 'AnelmaGeneral']);
    const { enqueueSnackbar } = useSnackbar();

    const context = useAppSelector((state) => state.context);
    const user = useAppSelector((state) => state.loginStatus);
    const parsedContext = contextUtils.parseFarmContext(context.data.currentContext?.context || '');

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [pickupPlace, setPickupPlace] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [selectedCarcassOption, setSelectedCarcassOption] = useState<string | null>(null);

    const questions: ProducerReturnQuestion[] = [
        { type: ProducerReturnOptionResourceText.insideBeef, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.roastBeef, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.outsideBeef, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.cornerBeef, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.insideFillet, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.outsideFillet, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.entrecote, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.shankMeat, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.flankSteak, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.ribs, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.breastOrBrisket, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.lapBeef, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.frontPartOfBack, optionParameterName: 'MeatCutStyle', defaultValue: MeatCutStyleEnum.PremiumCuts },
        { type: ProducerReturnOptionResourceText.bones, optionParameterName: 'ReturnChoice', defaultValue: ReturnChoiceEnum.DontTake },
        { type: ProducerReturnOptionResourceText.fat, optionParameterName: 'ReturnChoice', defaultValue: ReturnChoiceEnum.DontTake },
    ];
    const answerOptions = () => questions.map((q) => { return { type: q.type, value: q.defaultValue } });
    const [selectedCutOptions, setSelectedCutOptions] = useState<{ type: number, value: number }[]>(answerOptions());
    const [answerParametersByName, setAnswerParametersByName] = useState<Record<string, AInputItem<ARadioButtonGroupInputValue>[]>>({
        MeatCutStyle: [],
        ReturnChoice: [],
    });

    const [additionalInfo, setAdditionalInfo] = useState<string>('');

    useEffect(() => {
        setIsLoading(true);
        paramRepository
            .load([
                'AnelmaGeneral',
                'AnelmaProducerReturn',
                'AnelmaLayout',
            ])
            .then(() => {
                setAnswerParametersByName({
                    MeatCutStyle: paramRepository
                        .resource('AnelmaProducerReturn', 'MeatCutStyle')
                        .map(r => { return { text: r.text, value: r.code }; }),
                    ReturnChoice: paramRepository
                        .resource('AnelmaProducerReturn', 'ReturnChoice')
                        .map(r => { return { text: r.text, value: r.code }; }),
                });
            })
            .finally(() => {
                if (!isNew && data) {
                    setPickupPlace(data.DeliveryPlace);
                    setPhoneNumber(data.PhoneNumber);
                    setEmail(data.EmailAddress);

                    let i = data.Answers.findIndex(a => a.Type === ProducerReturnOptionResourceText.selectedCarcassOption);

                    if (i != -1)
                        setSelectedCarcassOption(data.Answers[i].Answer);

                    const answerMap = new Map<number, number>(
                        data.Answers
                            .filter(a => a.Type !== ProducerReturnOptionResourceText.selectedCarcassOption)
                            .map(a => [a.Type, Number.parseInt(a.Answer)])
                    );

                    setSelectedCutOptions(
                        answerOptions().map((o) => {
                            if (answerMap.has(o.type))
                                return { ...o, value: answerMap.get(o.type) as number };

                            return o;
                        })
                    );

                    setAdditionalInfo(data.AdditionalInfo);
                }

                setIsLoading(false);
            });
    }, []);

    const getProducerNumber = () => {
        let producerNumber = '';
        let i = farm.Aliases.findIndex(a => a.Type === AliasTypeEnum.ProducerNumber);

        if (i != -1)
            producerNumber = farm.Aliases[i].Value;

        return producerNumber;
    }

    const getSelectedAnimalString = () => {
        let euIdentifier = '';

        if (!isNew && data)
            euIdentifier = data.EUIdentifier;
        else if (selectedAnimalEuIdentifier)
            euIdentifier = selectedAnimalEuIdentifier;

        return t('AnelmaProducerReturn:1031') + ' ' + euIdentifier;
    }

    const save = () => {
        if (validator.invalid) {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });

            return;
        }

        setIsLoading(true);
        let payload: IProducerReturn = {
            Id: null,
            AnnouncementId: announcementId,
            ProducerName: user.data.firstName + ' ' + user.data.lastName,
            PhoneNumber: phoneNumber,
            EmailAddress: email,
            DeliveryWeek: (!isNew && data)
                ? data.DeliveryWeek
                : moment(props.selectedAnimalCollectDate).add(1, 'week').week(), // rolls over to week 1 of the next year
            DeliveryPlace: pickupPlace,
            AdditionalInfo: additionalInfo,
            EUIdentifier: selectedAnimalEuIdentifier ?? '',
            Answers: selectedCutOptions.map(o => { return { Type: o.type, Answer: o.value.toString() } }),
        }

        payload.Answers.push({ Type: ProducerReturnOptionResourceText.selectedCarcassOption, Answer: selectedCarcassOption ?? '' })

        if (isNew) {
            producerReturnApi
                .createProducerReturn(payload)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });

                        return;
                    }

                    enqueueSnackbar(t('AnelmaGeneral:1018'), {
                        variant: 'success',
                    });
                    onClose();
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        else if (data) {
            payload.Id = data.Id;
            payload.DeliveryWeek = data.DeliveryWeek;
            payload.EUIdentifier = data.EUIdentifier;
            payload.ProducerName = data.ProducerName;

            producerReturnApi
                .updateProducerReturn(payload)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });

                        return;
                    }

                    enqueueSnackbar(t('AnelmaGeneral:1018'), {
                        variant: 'success',
                    });
                    onClose();
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }

    const remove = () => {
        setIsLoading(true);
        if (data && data.Id) {
            producerReturnApi
                .deleteProducerReturn(data.Id)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });

                        return;
                    }

                    setIsLoading(false);
                    enqueueSnackbar(t('AnelmaGeneral:1018'), {
                        variant: 'success',
                    });
                    onClose();
                });
        }
    }

    const saveButtonDisabled = () => {
        if (isNew)
            return !auth.canCreateProducerReturn;

        return !auth.canUpdateProducerReturn;
    }

    const validPhonNumberTypes: string[] = [
        '+358 40 1234567',
        '+358 (0)40 1234567',
        '(040) 1234567',
        '040-1234567',
        '0401234567',
    ];
    const phoneNumberTooltipText = (
        <span style={{ whiteSpace: 'pre-line' }}>{`${t(
            'AnelmaGeneral:1028'
        )} \n ${validPhonNumberTypes.join('\n')}`}</span>
    );

    const meatReturnGeneralInformation = () => {
        return (
            <>
                <Grid container>
                    <Grid size={{ md: 6 }}>
                        <ATextInput
                            id={'producer-return-delivery-week'}
                            label={t('AnelmaProducerReturn:1001')}
                            onChange={(v) => null}
                            validator={validator}
                            disabled
                            value={
                                (!isNew && data)
                                    ? data.DeliveryWeek.toString()
                                    : moment(props.selectedAnimalCollectDate).add(1, 'week').week().toString() // rolls over to week 1 of the next year
                            }
                        />
                    </Grid>
                    <Grid size={{ md: 6 }}>
                        <ATextInput
                            id={'producer-return-pickup-point'}
                            label={t('AnelmaProducerReturn:1002')}
                            onChange={(v) => setPickupPlace(v)}
                            validator={validator}
                            value={pickupPlace}
                            disabled={readonly}
                            required
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid size={{ md: 6 }}>
                        <Tooltip title={phoneNumberTooltipText}>
                            <ATextInput
                                id={'producer-return-phone-number'}
                                label={t('AnelmaProducerReturn:1003')}
                                onChange={(v) => setPhoneNumber(v)}
                                validator={validator}
                                value={phoneNumber}
                                disabled={readonly}
                                regExp={
                                    /^(\d+)$|^(\d+[-]\d+)$|^([(]\d+[)][ ]\d+)$|^([+]\d+[ ]\d+[ ]\d+)$|^([+]\d+[ ][(][0][)]\d+[ ]\d+)$/
                                }
                                required
                            />
                        </Tooltip>
                    </Grid>
                    <Grid size={{ md: 6 }}>
                        <AEmailAddressInput
                            id={'producer-return-email'}
                            label={t('AnelmaProducerReturn:1026')}
                            onChange={(v) => setEmail(v)}
                            validator={validator}
                            value={email}
                            disabled={readonly}
                            required
                        />
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid size={{ md: 6 }}>
                        <ATextInput
                            id={'producer-return-producer-number'}
                            label={t('AnelmaProducerReturn:1004')}
                            onChange={(v) => null}
                            validator={validator}
                            disabled
                            value={getProducerNumber()}
                        />
                    </Grid>
                    <Grid size={{ md: 6 }}>
                        <ATextInput
                            id={'producer-return-producer-name'}
                            label={t('AnelmaProducerReturn:1005')}
                            onChange={(v) => null}
                            validator={validator}
                            disabled
                            value={
                                (!isNew && data)
                                    ? data.ProducerName
                                    : user.data.firstName + ' ' + user.data.lastName
                            }
                        />
                    </Grid>
                </Grid>
            </>
        );
    }

    const getRightActions: () => ADialogButtonProp[]
        = () => {
            let temp: ADialogButtonProp[] = [];

            if (!isNew && data) {
                temp.push({
                    label: t('AnelmaProducerReturn:1029'),
                    onClick: () => remove(),
                    type: 'delete',
                    disabled: readonly || !auth.canDeleteProducerReturn
                });

                if (data.Id)
                    temp.push({
                        label: 'PDF',
                        onClick: () => printingApi.printProducerReturn((data.Id as string)),
                        type: 'default',
                    });
            }

            temp.push(
                {
                    label: t('AnelmaProducerReturn:1024'),
                    onClick: () => save(),
                    type: 'save',
                    disabled: saveButtonDisabled(),
                }
            );

            return temp;
        }

    const footerActionButtons: ADialogButtonsProp = {
        left: [
            {
                onClick: () => props.onClose(),
                type: 'cancel',
            },
        ],
        right: getRightActions()
    };

    return (
        <ADialog open onClose={props.onClose} >
            <ADialogTitle>{t('AnelmaProducerReturn:1000')}</ADialogTitle>

            <ADialogContent size='md' isLoading={isLoading}>
                {meatReturnGeneralInformation()}

                <AInputContainer>
                    <Typography variant='h4' style={{ marginTop: '40px' }}>{t('AnelmaProducerReturn:1030')}</Typography>
                    <Typography variant='h6' style={{ marginTop: '20px' }}>
                        {getSelectedAnimalString()}
                    </Typography>
                </AInputContainer>

                <ADropdown
                    id={'producer-return-carcass-options'}
                    //label={t('AnelmaCompany:1007')}
                    onChange={(v) => setSelectedCarcassOption(v as string)}
                    parameterName={'AnelmaProducerReturn:AnimalPart'}
                    validator={validator}
                    value={selectedCarcassOption}
                    disabled={readonly}
                />

                {questions.map((q, i) => {
                    let text = t('AnelmaProducerReturn:' + q.type);

                    return (
                        <Grid container style={{ paddingLeft: '40px' }}>
                            <Grid size={{ sm: 4 }} style={{ display: 'flex', alignItems: 'end' }}>
                                <Typography variant='body1'>{text}</Typography>
                            </Grid>
                            <Grid size={{ sm: 4 }}>
                                <ARadioButtons
                                    id={`producer-return-cut-options-${i}`}
                                    onChange={(val) => {
                                        setSelectedCutOptions((prev) => {
                                            return prev.map(p => {
                                                if (p.type === q.type)
                                                    return { ...p, value: Number(val) };
                                                return p;
                                            });
                                        })
                                    }}
                                    items={answerParametersByName[q.optionParameterName]}
                                    validator={validator}
                                    value={selectedCutOptions.find(o => o.type === q.type)?.value.toString() ?? q.defaultValue.toString()}
                                    disabled={readonly}
                                    withoutContainer
                                />
                            </Grid>
                        </Grid>
                    );
                })}

                <ATextarea
                    id={'producer-return-additional-info'}
                    style={{ marginBottom: '20px', marginTop: '20px' }}
                    label={t('AnelmaProducerReturn:1027')}
                    onChange={(v) => setAdditionalInfo(v)}
                    validator={validator}
                    value={additionalInfo}
                    disabled={readonly}
                />

                <Typography variant='body2' style={{ textAlign: 'center' }}>{t('AnelmaProducerReturn:1022')}</Typography>
            </ADialogContent>
            <ADialogActions buttons={footerActionButtons}></ADialogActions>
        </ADialog>
    );
}
